import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { DollarSign, ShoppingBag, BookOpen, Loader2, Receipt, Package } from 'lucide-react';

const statusColors = {
  completed: 'bg-green-100 text-green-700',
  confirmed: 'bg-green-100 text-green-700',
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  initiated: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
};

export default function PurchaseHistoryPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/payments/history/')
      .then(r => setData(r.data))
      .catch(() => setData({ payments: [], enrollments: [], total_spent: 0, total_enrollments: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const allItems = [
    ...(data?.payments || []).map(p => ({ ...p, type: 'payment', sortDate: p.date })),
    ...(data?.enrollments || []).map(e => ({ ...e, type: 'enrollment', sortDate: e.date })),
  ].sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Receipt className="w-6 h-6" /> Purchase History
        </h1>
        <p className="text-muted-foreground">Your payment and enrollment history</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">${(data?.total_spent || 0).toFixed(2)}</p>
          <p className="text-xs text-muted-foreground">Total Spent</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{data?.payments?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Payments</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{data?.total_enrollments || 0}</p>
          <p className="text-xs text-muted-foreground">Enrollments</p>
        </CardContent></Card>
      </div>

      {/* History List */}
      {allItems.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-semibold">No purchase history yet</p>
          <p className="text-sm">Your payments and enrollments will appear here.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {allItems.map((item, i) => (
            <Card key={i} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                {item.type === 'payment' ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold">Payment</p>
                        {(item.items || []).length > 0 ? (
                          <div className="mt-1 space-y-0.5">
                            {item.items.map((ci, j) => (
                              <p key={j} className="text-sm text-muted-foreground">
                                {ci.course_title || ci.name || 'Course'} {ci.learner_name ? `— ${ci.learner_name}` : ''}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Stripe Payment</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">${item.amount.toFixed(2)}</p>
                      <Badge className={statusColors[item.status] || 'bg-gray-100'}>{item.status}</Badge>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{item.course}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.learner}{item.kit_purchased ? ' · Kit included' : ''}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {(item.price > 0 || item.kit_price > 0) && (
                        <p className="text-lg font-bold">${(item.price + item.kit_price).toFixed(2)}</p>
                      )}
                      {item.price === 0 && item.kit_price === 0 && (
                        <p className="text-sm text-muted-foreground">Free</p>
                      )}
                      <Badge className={statusColors[item.status] || 'bg-gray-100'}>{item.status}</Badge>
                      {item.kit_purchased && (
                        <div className="mt-1"><Badge className="bg-amber-100 text-amber-700 text-[10px]"><Package className="h-3 w-3 mr-0.5 inline" /> Kit</Badge></div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
